
import React, { useState } from "react";
import { Navigation } from "@/components/layout/Navigation";
import { ClassesHeader } from "@/components/classes/ClassesHeader";
import { ClassesList } from "@/components/classes/ClassesList";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { useAvailableClasses } from "@/hooks/useAvailableClasses";
import { useClassSubscriptions } from "@/hooks/useClassSubscriptions";
import { GymCodeForm } from "@/components/settings/profile/GymCodeForm";
import { LoadingState } from "@/components/classes/LoadingStates";
import { TutorialButton } from "@/components/intro/TutorialButton";
import { IntroTour } from "@/components/intro/IntroTour";
import { ReservationsSection } from "@/components/reservations/ReservationsSection";
import { UserReservations } from "@/components/reservations/UserReservations";
import { MisHorarios } from "@/components/schedules/MisHorarios";
import { Dumbbell } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, isAfter } from "date-fns";
import { filterClassesByTier } from "@/utils/evolutionSportAccess";

const Clases = () => {
  const { data: profile, isLoading: profileLoading } = useProfileQuery();
  const { data: classes, isLoading: classesLoading } = useAvailableClasses();
  const { myClasses, loadingMyClasses, subscribeMutation, unsubscribeMutation } = useClassSubscriptions();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeView, setActiveView] = useState('all');
  const [showTutorial, setShowTutorial] = useState(false);

  // Fetch gym name for Evolution Sport filtering
  const { data: gym } = useQuery({
    queryKey: ["gym", profile?.gym_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gyms")
        .select("name")
        .eq("id", profile!.gym_id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.gym_id,
  });

  // Fetch active payment tier (only for socios)
  const { data: activeTier } = useQuery({
    queryKey: ["active-tier", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payments")
        .select("*")
        .eq("user_id", profile!.id)
        .order("paid_at", { ascending: false });
      if (error) throw error;

      const lastPayment = data?.[0];
      if (!lastPayment) return null;

      const isActive = isAfter(
        addDays(new Date(lastPayment.paid_at!), lastPayment.validity_days),
        new Date()
      );
      if (!isActive || !lastPayment.description) return null;

      const desc = lastPayment.description.toUpperCase();
      const tiers = ["BLACK", "PLATINUM", "GOLD", "SILVER"];
      for (const tier of tiers) {
        if (desc.includes(tier)) return tier;
      }
      return null;
    },
    enabled: !!profile?.id && profile?.role === "socio",
  });

  const isLoading = profileLoading || classesLoading || loadingMyClasses;

  if (isLoading) {
    return <LoadingState />;
  }

  // Apply tier-based filtering only for socios
  const filteredByTier = profile?.role === "socio"
    ? filterClassesByTier(classes || [], activeTier ?? undefined, gym?.name)
    : classes || [];

  const isSubscribed = (classId: string) => {
    return myClasses?.some(subscription => subscription.id === classId) || false;
  };

  const handleClassAction = (classId: string) => {
    if (isSubscribed(classId)) {
      unsubscribeMutation.mutate(classId);
    } else {
      subscribeMutation.mutate(classId);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-foreground pb-24">
      <TutorialButton onClick={() => setShowTutorial(true)} />
      <IntroTour run={showTutorial} setRun={setShowTutorial} />
      
      <main className="container px-3 sm:px-4 lg:px-6">
        {!profile?.gym_id ? (
          <div className="pt-16 px-4">
            <header className="mb-8 text-center animate-fade-in">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-primary/20">
                  <Dumbbell className="w-8 h-8 text-primary" />
                </div>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">
                Clases
              </h1>
            </header>
            <div className="glass-card p-6 text-center text-foreground space-y-4">
              <p>Para ver las clases disponibles, primero debes unirte a un gimnasio</p>
              <GymCodeForm />
            </div>
          </div>
        ) : (
          <>
            {(() => {
              const isLaBarraGym = gym?.name?.toLowerCase().includes('la barra');
              return (
                <>
                  <ClassesHeader 
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    activeView={activeView}
                    setActiveView={setActiveView}
                    canSubscribe={true}
                    isTeacher={profile?.role === "profesor"}
                    showSearch={activeView !== 'reservations' && activeView !== 'my-reservations' && activeView !== 'schedules'}
                    showScheduleTab={isLaBarraGym}
                  />
                  {activeView === 'reservations' ? (
                    <ReservationsSection />
                  ) : activeView === 'my-reservations' ? (
                    <UserReservations onBack={() => setActiveView('all')} />
                  ) : activeView === 'schedules' ? (
                    <MisHorarios />
                  ) : (
                    <ClassesList 
                      classes={filteredByTier}
                      isLoading={isLoading}
                      searchQuery={searchQuery}
                      isSubscribed={isSubscribed}
                      handleClassAction={handleClassAction}
                      subscribeMutation={subscribeMutation}
                      unsubscribeMutation={unsubscribeMutation}
                      activeView={activeView}
                      userRole={profile?.role}
                      userProfile={profile}
                    />
                  )}
                </>
              );
            })()}
          </>
        )}
        <Navigation />
      </main>
    </div>
  );
};

export default Clases;
